import React from 'react';
import { Mail, Phone, Facebook, Instagram, Linkedin, Link2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AgentConfig } from '../config/agents';

interface HeroProps {
  agent: AgentConfig;
}

export default function Hero({ agent }: HeroProps) {
  const navigate = useNavigate();

  return (
    <section className="relative bg-white py-8 md:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:min-h-[600px]">
          
          {/* Image Column - 5/12 width on desktop */}
          <div className="md:col-span-5 h-full">
            <div className="relative w-full h-full flex items-stretch">
              <div className="relative w-full h-full rounded-lg overflow-hidden shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-b from-navy-900/10 to-transparent"></div>
                <img
                  src="/agentphoto.png"
                  alt={`${agent.name} - ${agent.title}`}
                  className="object-cover w-full h-full grayscale transform transition-all duration-300 hover:scale-[1.02] hover:grayscale-0"
                  loading="eager"
                />
              </div>
            </div>
          </div>

          {/* Content Column - 7/12 width on desktop */}
          <div className="md:col-span-7 flex flex-col justify-between bg-navy-900 text-white p-6 md:p-8 lg:p-10 rounded-lg shadow-lg">
            {/* Top content */}
            <div className="space-y-4">
              <div className="mb-4">
                <h1 className="text-2xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold animate-slide-right">
                  <span className="inline-block">Hi! I'm</span>{' '}
                  <span className="inline-block">{agent.name}.</span>
                </h1>
              </div>
              <p className="text-gray-200 text-lg sm:text-xl leading-relaxed animate-slide-right animate-delay-100">
                {agent.heroDescription}
              </p>
              
              {/* Contact info with consistent spacing */}
              <div className="hero-contact-info space-y-4 mb-8">
                <a
                  href={`mailto:${agent.email}`}
                  className="group flex items-center text-accent hover:text-white text-lg transition-all duration-200 animate-slide-right animate-delay-200"
                  aria-label={`Email ${agent.name}`}
                >
                  <Mail className="w-6 h-6 mr-3 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
                  <span className="truncate group-hover:translate-x-1 transition-transform duration-200">
                    {agent.email}
                  </span>
                </a>
                <a 
                  href={`tel:${agent.phone.replace(/[^0-9]/g, '')}`}
                  className="group flex items-center text-accent hover:text-white text-lg transition-all duration-200 animate-slide-right animate-delay-300"
                  aria-label={`Call ${agent.name}`}
                >
                  <Phone className="w-6 h-6 mr-3 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
                  <span className="truncate group-hover:translate-x-1 transition-transform duration-200">
                    {agent.phone}
                  </span>
                </a>
              </div>
              
              {/* Empty div for spacing - 24px height */}
              <div className="h-6"></div>
            </div>

            {/* Bottom content - buttons and social links */}
            <div className="space-y-4 mt-0">
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href={agent.socialLinks.retter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center h-12 bg-accent text-white rounded-lg hover:bg-accent/90 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 px-6 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent focus:ring-offset-navy-900 whitespace-nowrap"
                >
                  Listings
                </a>
                <button 
                  onClick={() => navigate('/contact')}
                  className="flex-1 flex items-center justify-center h-12 border-2 border-accent text-white rounded-lg hover:bg-accent/10 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 px-6 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent focus:ring-offset-navy-900 whitespace-nowrap"
                >
                  Contact Me
                </button>
              </div>
              
              {/* Social icons with consistent spacing */}
              <div className="flex items-center space-x-5 pt-2">
                {agent.socialLinks.facebook && (
                  <a
                    href={agent.socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                    aria-label="Facebook Profile"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                )}
                {agent.socialLinks.instagram && (
                  <a
                    href={agent.socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                    aria-label="Instagram Profile"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                )}
                {agent.socialLinks.linkedin && (
                  <a
                    href={agent.socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                    aria-label="LinkedIn Profile"
                  >
                    <Linkedin className="w-6 h-6" />
                  </a>
                )}
                {agent.socialLinks.zillow && (
                  <a
                    href={agent.socialLinks.zillow}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                    aria-label="Zillow Profile"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 23.283 25.577"
                      className="w-6 h-6"
                      fill="currentColor"
                    >
                      <g fillRule="nonzero">
                        <path d="m15.743 6.897c.117-.026.169.013.24.091.403.448 1.691 2.021 2.041 2.45.065.078.02.163-.032.208-2.6 2.028-5.493 4.901-7.105 6.955-.032.046-.006.046.02.039 2.808-1.209 9.405-3.14 12.376-3.679v-3.763l-11.628-9.198-11.648 9.191v4.114c3.607-2.144 11.953-5.466 15.736-6.408z" />
                        <path d="m6.279 22.705c-.097.052-.176.039-.254-.039l-2.171-2.587c-.058-.072-.065-.111.013-.221 1.678-2.457 5.103-6.286 7.287-7.904.039-.026.026-.059-.02-.039-2.275.741-8.742 3.523-11.134 4.875v8.787h23.277v-8.462c-3.172.539-12.675 3.367-16.998 5.59z" />
                      </g>
                    </svg>
                  </a>
                )}
                <a
                  href={agent.socialLinks.retter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-white hover:scale-110 transition-all duration-200"
                  aria-label="Retter & Company Profile"
                >
                  <Link2 className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}